/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2024 - All Rights Reserved
###################################################################
*/
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions */

import React, { useState, useEffect, useRef, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from "formik";
import * as yup from 'yup';

import { GlobalAuthContext, GlobalLoaderContext } from "store/context";
import { FitinsurAPI, MessageCenter } from "utils";
import Loader from "components/Loader";
import Colors from "constants/Colors";

import Layout from 'components/Layout';
import Tabs from 'components/Tabs';

import BasicInformation from './BasicInformation';
import Teams from './Teams';
import { GeneralSection, TeamsSection } from './sections/index';
import { HierarchySection } from './sections/index';

import {
  useGetQuestionnairesByIdProduct
} from "@fitinsur/services/QuestionnairesService";

import {
  useUpdatePersonRelationshipsHierarchy
} from "@fitinsur/services/PersonRelationshipsService";

import {
  useGetTeams,
  useUpdateTeamsAsync
} from "@fitinsur/services/TeamsService";

export default function PersonManagement(props) {
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();

  const formikRef = useRef(null);
  const refQuestionnaireSent = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();

  const [questionnaire, setQuestionnaire] = useState([]);
  const [idPerson, setIdPerson] = useState(props.match?.params?.idPerson);
  const [personData, setPersonData] = useState(null);
  const [teamsGroup, setTeamsGroup] = useState([]);
  const [teamsGroupToDelete, setTeamsGroupToDelete] = useState([]);
  const [hierarchyGroup, setHierarchyGroup] = useState([]);
  const [contactsGroup, setContactsGroup] = useState([]);
  const [contactsGroupToDelete, setContactsGroupToDelete] = useState([]);
  const [forceLoading, setForceLoading] = useState(false);

  let SECTIONS = {
    TEAMS: { name: "TEAMS", isMultiple: true, isRisk: false },
  };

  const [validations, setValidations] = useState({});

  const [initial, setInitial] = useState({
    shortName: '',
    fullName: '',
    personType: '',
    regulatoryId: '',
    legalId: '',
    legalIdType: {
      idDomain: '',
      domainDescription: '',
      idDomainType: '',
    },
    contactType: '',
    description: '',
    street: '',
    number: '',
    complement: '',
    zipCode: '',
    district: '',
    city: '',
    state: '',
    country: '',
    countrySubdivision: '',
    sicCode: '',
    rating: '',
    //personStatus: ''
  });

  const tabs = [
    {
      label: 'Basic Information',
      content: BasicInformation
    },
    {
      label: 'Teams',
      content: Teams
    },
  ];

  const {
    data: getQuestionnaireData,
    loading: getQuestionnaireLoading,
    status: getQuestionnaireStatus,
    request: getQuestionnaireRequest,
  } = useGetQuestionnairesByIdProduct(58);

  const {
    request: updatePersonRelationshipsHierarchyRequest,
    loading: updatePersonRelationshipsHierarchyLoading
  } = useUpdatePersonRelationshipsHierarchy(idPerson);

  const {
    data: useGetTeamsData,
    loading: useGetTeamsLoading,
    status: useGetTeamsStatus,
    request: useGetTeamsRequest,
  } = useGetTeams(idPerson);

  const {
    request: useUpdateTeamsRequest,
    loading: useUpdateTeamsLoading
  } = useUpdateTeamsAsync(idPerson);

  const getPerson = async () => {
    try {
      if (!idPerson) return;

      loading.start();
      setForceLoading(true);
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetPersonsInfo(idPerson).then(res => res.json())
      if (response.ret_code !== 200) mc.error(response.ret_msg_details);

      loading.stop();
      let {
        firstName,
        lastName,
        legalPersonId,
        susepNumber,
        personType,
        legalIdType,
        hierarchy,
        parentCompany,
        personRelationships,
        legalEntity,
        sicCode,
        rating,
        idPersonRoot
      } = response.ret_value;

      formikRef.current.values.shortName = firstName || "";
      formikRef.current.values.fullName = lastName || "";
      formikRef.current.values.legalPersonId = legalPersonId || "";
      formikRef.current.values.regulatoryId = susepNumber || 'N/A';
      formikRef.current.values.personType = personType || "";
      formikRef.current.values.legalIdType = legalIdType || "";
      formikRef.current.values.rating = rating || "";
      formikRef.current.values.sicCode = sicCode?.sicDescription || "";
      formikRef.current.values.hierarchy = hierarchy;
      formikRef.current.values.parentCompany = parentCompany?.lastName;
      formikRef.current.values.parentCompanyId = parentCompany?.idPerson;
      formikRef.current.values.legalEntity = legalEntity?.lastName;
      formikRef.current.values.legalEntityId = legalEntity?.idPerson;
      formikRef.current.values.idPersonRoot = idPersonRoot || "";

      const relationships = {
        approvedMarkets: personRelationships[314] || [],
        clientSuppliers: personRelationships[290] || [],
        employers: personRelationships[291] || [],
        insurers: personRelationships[365] || [],

        branches: personRelationships[379] || [],

        surplusBrokers: personRelationships[448] || [],
        thirdPartyBrokers: personRelationships[449] || [],
        banks: personRelationships[380] || [],
        governmentTaxes: personRelationships[616] || [],
        runOff: personRelationships[628] || []
      }

      relationships.insurers = relationships.insurers.map((insurer) => {
        const statusRunOff = relationships?.runOff?.find(statusRunOff => insurer?.idPersonTail == statusRunOff?.idPersonTail);

        const newInsurer = { ...insurer };
        newInsurer.relationshipContent.runOff = statusRunOff ? true : false;

        if (statusRunOff) {
          newInsurer.idPersonRelationshipRunOff = statusRunOff?.idPersonRelationship;
        }
        return newInsurer;
      });

      setPersonData(response.ret_value);
      setForceLoading(false);
    } catch (error) {
      console.log('ERROR', error);
      loading.stop();
      setForceLoading(false);
      mc.error("An error happened, please try again.");
    };
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      hierarchy: yup.string().required("* Required field"),
      shortName: yup.string().required("* Required field"),
      fullName: yup.string().required("* Required field"),
      personType: yup.string().required("* Required field"),
      sicCode: formikRef.current.values.personType?.idDomain === 35 ? yup.string().required("* Required field") : yup.string().notRequired(),
      regulatoryId: yup.string().notRequired(),
      legalPersonId: yup.string().notRequired(),
      legalIdType: yup.string().notRequired(),
      street: yup.string().notRequired(),
      number: yup.string().notRequired(),
      complement: yup.string().notRequired(),
      zipCode: yup.string().when('hierarchy', (hierarchy) => idPerson || hierarchy !== 'Legal Entity' ? yup.string().notRequired() : yup.string().required("* Required field")),
      district: yup.string().notRequired(),
      city: yup.string().notRequired(),
      state: yup.string().when('hierarchy', (hierarchy) => formikRef.current.values.personType?.idDomain === 615 && hierarchy === 'Legal Entity' ? yup.string().required("* Required field") : yup.string().notRequired()),
      country: yup.string().when('street', (street) => idPerson || !street ? yup.string().notRequired() : yup.string().required("* Required field")),
      countrySubdivision: yup.string().notRequired(),
      parentCompany: yup.string().when('hierarchy', (hierarchy) => hierarchy === 'Holding' ? yup.string().notRequired() : yup.string().required("* Required field")),
      legalEntity: yup.string().when('hierarchy', (hierarchy) => hierarchy !== 'Individual' ? yup.string().notRequired() : yup.string().required("* Required field")),
      email: yup.string().email().notRequired(),
    });
    setValidations(validations);
    return validations
  }

  const loadContent = async () => {
    setIdPerson(props.match?.params?.idPerson)
    useGetTeamsRequest();
    getQuestionnaireRequest(null, {
      idDomain: 752,
      idDomainStatus: 451
    });
    await createValidation();
    await getPerson();
    loading.stop();
  }

  const updateTeams = async () => {
    if (hierarchyGroup?.length > 0) {
      await updatePersonRelationshipsHierarchyRequest({
        tailPersonsContent: HierarchySection.events.saveHierarchyOrder(hierarchyGroup),
      });
    }

    const teamsData = TeamsSection.events.handleTeamsData({ teamsGroup, contactsGroup });

    await useUpdateTeamsRequest({
      teams: teamsData,
      teamsRemoved: teamsGroupToDelete,
      contactsRemoved: contactsGroupToDelete
    });

    loadContent();
  }

  useEffect(() => {
    loadContent();
  }, [initial]);

  useEffect(() => {
    setIdPerson(props.match?.params?.idPerson);
    loadContent();
  }, [props.match.params?.idPerson])

  useEffect(() => {
    if (
      getQuestionnaireLoading ||
      updatePersonRelationshipsHierarchyLoading ||
      forceLoading ||
      useGetTeamsLoading ||
      useUpdateTeamsLoading
    ) {
      loading.start();
    } else {
      loading.stop();
    }
  }, [
    getQuestionnaireLoading,
    updatePersonRelationshipsHierarchyLoading,
    forceLoading,
    useGetTeamsLoading,
    useUpdateTeamsLoading
  ]);

  useEffect(() => {
    if (getQuestionnaireStatus === 200 && useGetTeamsStatus === 200 && personData) {
      const teams = useGetTeamsData?.value.sort((a, b) => a?.relationshipContent?.order - b?.relationshipContent?.order);

      GeneralSection.events.handleQuestionnaire({
        data: getQuestionnaireData?.value,
        setQuestionnaire,
        setHierarchyGroup,
        setTeamsGroup,
        setFieldValue: formikRef.current.setFieldValue,
        personData,
        setContactsGroup,
        teams
      });
    }
  }, [getQuestionnaireStatus, useGetTeamsStatus, personData]);

  return (
    <Formik
      initialValues={initial}
      innerRef={formikRef}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
    >
      {(formikProps) => (
        <Layout
          idOperation={props?.idOperation}
          title={formikProps.values.fullName}
        >
            <Tabs
              tabs={tabs.filter((tab) => {
                if (tab.label === 'Teams' && formikRef?.current?.values?.idPersonRoot) return false;
                return true;
              }).map(tab => ({
                ...tab,
                content: tab?.content &&
                  <tab.content
                    idPerson={idPerson}
                    formikProps={formikProps}
                    formikRef={formikRef}
                    idProduct={58}
                    globalQuestionnaire={questionnaire}
                    setGlobalQuestionnaire={setQuestionnaire}
                    teamsGroup={teamsGroup}
                    setTeamsGroup={setTeamsGroup}
                    teamsGroupToDelete={teamsGroupToDelete}
                    setTeamsGroupToDelete={setTeamsGroupToDelete}
                    hierarchyGroup={hierarchyGroup}
                    setHierarchyGroup={setHierarchyGroup}
                    contactsGroup={contactsGroup}
                    setContactsGroup={setContactsGroup}
                    contactsGroupToDelete={contactsGroupToDelete}
                    setContactsGroupToDelete={setContactsGroupToDelete}
                    SECTIONS={SECTIONS}
                    refQuestionnaireSent={refQuestionnaireSent}
                    submitFunction={updateTeams}
                  />
              }))}
            />
          
          {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
        </Layout>
      )}
    </Formik>
  )
}